import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { tenantManagementApi } from "./tenant-api";
import { GLOBAL_TENANT_FETCH_MIN_PAGE_SIZE } from "./tenant-constants";

interface TenantManagementState {
  tenantManagement: ITenant[];
  selectedTenant?: ITenant;
  areTenantsLoading: boolean;
}

type TenantQueryArgs = {
  pageNo: number;
  pageSize: number;
  searchKeyword?: string;
};

type TenantQueryAction = {
  meta?: {
    arg?: {
      originalArgs?: TenantQueryArgs;
    };
  };
};

const isGlobalTenantFetch = (action: TenantQueryAction): boolean => {
  const args = action.meta?.arg?.originalArgs;
  const searchKw = (args?.searchKeyword ?? "").trim();

  return (
    args?.pageNo === 1 &&
    searchKw === "" &&
    (args?.pageSize ?? 0) >= GLOBAL_TENANT_FETCH_MIN_PAGE_SIZE
  );
};

const initialState: TenantManagementState = {
  tenantManagement: [],
  selectedTenant: undefined,
  areTenantsLoading: false,
};

export const tenantManagementSlice = createSlice({
  name: "tenantManagement",
  initialState,
  reducers: {
    setTenantManagement: (
      state,
      { payload }: PayloadAction<TenantManagementState["tenantManagement"]>
    ) => {
      state.tenantManagement = payload;
      state.areTenantsLoading = false;
    },
    clearTenantManagement: (state) => {
      state.tenantManagement = [];
    },
    setSelectedTenant: (state, action: PayloadAction<ITenant | undefined>) => {
      state.selectedTenant = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(
        tenantManagementApi.endpoints.getAllTenants.matchPending,
        (state, action) => {
          if (!isGlobalTenantFetch(action)) {
            return;
          }

          state.areTenantsLoading = true;
        }
      )
      .addMatcher(
        tenantManagementApi.endpoints.getAllTenants.matchFulfilled,
        (state, action) => {
          if (!isGlobalTenantFetch(action)) {
            return;
          }

          state.areTenantsLoading = false;
        }
      )
      .addMatcher(
        tenantManagementApi.endpoints.getAllTenants.matchRejected,
        (state, action) => {
          if (!isGlobalTenantFetch(action)) {
            return;
          }

          state.areTenantsLoading = false;
        }
      );
  },
});

export const { setTenantManagement, clearTenantManagement, setSelectedTenant } =
  tenantManagementSlice.actions;
